import type { MatrixSelectionKey } from '../components/WorkJourneyMonthMatrixGrid';
import { parseSelectionKey } from '../components/WorkJourneyMonthMatrixGrid';
import { isFutureMatrixCalendarDate } from './isFutureMatrixCalendarDate';

export type MatrixSelectionMeta = {
  userId: string;
  dates: string[];
  startDate: string;
  endDate: string;
  count: number;
  /** True when every selected day is adjacent (no gaps). */
  isContiguous: boolean;
};

/**
 * Floating action bar is future-only (AUJ / VAC / REM). Past and today open the
 * attendance drawer instead — keep the cell highlighted without showing the bar.
 */
export const shouldShowMatrixSelectionBar = (
  meta: MatrixSelectionMeta | null,
  nowMs: number = Date.now(),
): meta is MatrixSelectionMeta =>
  !!meta && isFutureMatrixCalendarDate(meta.startDate, nowMs);

const MS_PER_DAY = 86_400_000;

/** Sorted ISO dates are contiguous when each step is exactly one calendar day. */
export const isContiguousIsoDates = (sortedDates: string[]): boolean => {
  if (sortedDates.length <= 1) return true;
  for (let index = 1; index < sortedDates.length; index += 1) {
    const previous = Date.parse(`${sortedDates[index - 1]}T12:00:00Z`);
    const current = Date.parse(`${sortedDates[index]}T12:00:00Z`);
    if (!Number.isFinite(previous) || !Number.isFinite(current)) return false;
    if (current - previous !== MS_PER_DAY) return false;
  }
  return true;
};

/**
 * Derives single-user selection meta: discrete `dates[]` plus min–max bounds.
 * Returns null when empty or when more than one user is present.
 */
export const getMatrixSelectionMeta = (
  selectedKeys: Iterable<MatrixSelectionKey>,
): MatrixSelectionMeta | null => {
  const parsed = Array.from(selectedKeys)
    .map(parseSelectionKey)
    .filter((entry): entry is { userId: string; date: string } => entry != null);

  if (parsed.length === 0) {
    return null;
  }

  const userId = parsed[0].userId;
  if (parsed.some(entry => entry.userId !== userId)) {
    return null;
  }

  const dates = Array.from(new Set(parsed.map(entry => entry.date))).sort();
  return {
    userId,
    dates,
    startDate: dates[0],
    endDate: dates[dates.length - 1],
    count: dates.length,
    isContiguous: isContiguousIsoDates(dates),
  };
};

/** Keeps ⌘/Ctrl toggles locked to the first selected user. */
export const toggleMatrixSelectionKey = (
  previous: Set<MatrixSelectionKey>,
  userId: string,
  date: string,
  makeKey: (userId: string, date: string) => MatrixSelectionKey,
): Set<MatrixSelectionKey> => {
  const key = makeKey(userId, date);
  const next = new Set(previous);

  if (next.size === 0) {
    next.add(key);
    return next;
  }

  const meta = getMatrixSelectionMeta(next);
  if (meta && meta.userId !== userId) {
    return previous;
  }

  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  return next;
};
